/**
 * Portal access roles for the assessment-body app.
 *
 * - **chairman**: dashboard only (sidebar + deep links restricted).
 * - **admin**: full sidebar (default when not chairman).
 *
 * Chairman detection uses API `roles` and {@link CHAIRMAN_EMAIL_ALLOWLIST} in `global.constants.ts`.
 */

import { CHAIRMAN_EMAIL_ALLOWLIST, CHAIRMAN_ROLE_SLUG } from "./global.constants";

export const PORTAL_ROLES = {
	ADMIN: "admin",
	CHAIRMAN: "chairman",
} as const;

export type PortalRole = (typeof PORTAL_ROLES)[keyof typeof PORTAL_ROLES];

/** Normalize API `roles` (strings or `{ name: string }`) to lowercase slugs. */
export function parseRolesFromApi(roles: unknown): string[] {
	if (!Array.isArray(roles)) return [];
	const out: string[] = [];
	for (const r of roles) {
		if (typeof r === "string") {
			const s = r.trim().toLowerCase();
			if (s) out.push(s);
			continue;
		}
		if (r && typeof r === "object" && "name" in r && typeof (r as { name: unknown }).name === "string") {
			const s = String((r as { name: string }).name).trim().toLowerCase();
			if (s) out.push(s);
		}
	}
	return out;
}

export function resolvePortalRole(emailAddress: string | undefined, apiRoles: string[]): PortalRole {
	const email = (emailAddress ?? "").trim().toLowerCase();
	const roleSet = new Set(apiRoles.map(r => r.toLowerCase()));
	const chairmanSlug = CHAIRMAN_ROLE_SLUG.toLowerCase();

	if (roleSet.has(chairmanSlug)) return PORTAL_ROLES.CHAIRMAN;

	if (CHAIRMAN_EMAIL_ALLOWLIST.some(e => e.trim().toLowerCase() === email)) {
		return PORTAL_ROLES.CHAIRMAN;
	}

	return PORTAL_ROLES.ADMIN;
}
